/**
 * components/EmptyPanel
 * The quiet empty state: a dashed #CFCFCF border over #FAFAFA, `52px 24px`,
 * centred, with a 14px/500 title and a 12.5px grey line beneath.
 *
 * Distinct from EmptyState, and the difference is intentional. EmptyState
 * TEACHES — it is what a Finance screen shows when the org has never used the
 * feature. This is what a screen shows when the absence is simply good news
 * ("Queue clear · Nothing has crossed the threshold"), where a three-step
 * explanation would be condescending.
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading } from '../../theme/typography';

export interface EmptyPanelProps {
  title: string;
  hint?: string;
}

export function EmptyPanel({ title, hint }: EmptyPanelProps) {
  return (
    <View style={styles.panel}>
      <Text style={styles.title}>{title}</Text>
      {!!hint && <Text style={styles.hint}>{hint}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: neutral.borderDashed,
    borderRadius: radii.cardBlock,
    backgroundColor: neutral.surface2,
    paddingVertical: 52,
    paddingHorizontal: 24,
    alignItems: 'center',
    gap: 4,
  },
  title: { ...sans('500'), fontSize: fontSize.sm, color: neutral.ink },
  hint: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    textAlign: 'center',
  },
});

export default EmptyPanel;
